import moment from 'moment';
import { Feature, MultiPolygon, Polygon } from '@turf/turf';
import { ILAANCAdvisoryProperties } from './model/advisories/ILAANCAdvisoryProperties';
import { PersistableMissionFeatureCollection } from './model/PersistableMissionFeatureCollection';
import { ISubdividedMission } from './model/ISubdividedMission';
import { ISubdividableMissionProperties } from './model/ISubdividableMissionProperties';
import { IAirspaceClassification } from './model/authority-tiles/IAirspaceProperties';
import { ILAANCEanbledFor } from './model/authority-tiles/ILAANCProperties';
import { PolygonMerger } from './PolygonMerger';
import { AuthorizableLAANCAdvisoryProperties } from './model/advisories/AuthorizableLAANCAdvisoryProperties';
import { ILAANCStatusMeta } from './model/advisories/IAuthorizableLAANCAdvisoryProperties';
import { FeatureIntersector } from './FeatureIntersector';

export class MergeSubdividedMission {

    static inject = () => [PolygonMerger, FeatureIntersector];
    constructor(private polygonMerger: PolygonMerger, private featureIntersector: FeatureIntersector) { }

    merge = (mission: ISubdividedMission, initializedStatusMeta: ILAANCStatusMeta): PersistableMissionFeatureCollection => {

        const missionFeature: Feature<Polygon, ISubdividableMissionProperties> = mission.properties.mission;

        return new PersistableMissionFeatureCollection({
            features: [
                // NOTE: this too, should be refactored so the mission in in the feature collection
                // but this is one is not as high of a priority, since it is within the black box of AuthorizeMissionFacade
                missionFeature,
                ...mission.features,
                ...this.getAuthorizableLAANCAdvisoryFeatures(mission, initializedStatusMeta)
            ]
        });
    }

    /**
     * given a feature per UASFM grid id overlap
     * merges them so that we have one feature for each authorization we need
     * resulting in more than one feature
     *  - where airspaceIdent (UASFM_FAAID) varies
     *  - or airspace schedule (eg one uasfm feature might require 2 authorizations if the missions is from 9 - 12 but the airspace doesn't require authorization from 10 - 11)
     * so in effect the natural key is { airspaceIdent, startTime, endTime }
     *
     */
    private getAuthorizableLAANCAdvisoryFeatures = (mission: ISubdividedMission, initializedStatusMeta: ILAANCStatusMeta): Feature<Polygon, AuthorizableLAANCAdvisoryProperties>[] => {
        const result: Feature<Polygon, AuthorizableLAANCAdvisoryProperties>[] = [];

        const laancAdvisories = mission.features.filter(f => f.properties.source === 'uasfm+airspace' && f.properties.advisoryType === 'advise') as Feature<Polygon | MultiPolygon, ILAANCAdvisoryProperties>[];

        const airspaceIdents = laancAdvisories.map(a => a.properties.UASFM_FAAID).reduce(uniqueReducer, []);

        for (const airspaceIdent of airspaceIdents) {
            const identAdvisories = laancAdvisories.filter(f => f.properties.UASFM_FAAID === airspaceIdent);
            const airspaceGlobalId = identAdvisories[0].properties.AIRSPACE_GLOBAL_ID;

            const mergedPolygons = this.polygonMerger.merge(this.fanOutMultiPolygon(identAdvisories));

            for (const mergedPolygon of mergedPolygons) {
                // we can't assume that the output feature contains all the input features, eg if it was dis-contiguous
                // then we get more than one polygon and need to see whcih inputs went with which part of the dis-contiguous
                // polygons
                const relevantIdentAvisories = identAdvisories.filter(ident => this.featureIntersector.getIntersection(ident, mergedPolygon));

                mergedPolygon.properties = new AuthorizableLAANCAdvisoryProperties({
                    source: 'uasfm+authorizable',
                    operationID: '',
                    airspaceIdent,
                    startTime: mission.properties.mission.properties.startTime,
                    endTime: mission.properties.mission.properties.endTime,

                    uasfmGridIds: relevantIdentAvisories
                        .map(f => `${f.properties.UASFM_GLOBAL_ID}:${moment(f.properties.LAST_EDIT, 'M/D/YYYY').format('YYYY-MM-DD')}`)
                        .reduce(uniqueReducer, [])
                        .sort((a, b) => a.localeCompare(b)),

                    // for historical reasons, this is going down to a string where it is compatible with
                    // uniqueReducer, and then back out to an object
                    uasfmGridIdsV2: relevantIdentAvisories
                        .map(f => `${f.properties.UASFM_GLOBAL_ID}:${moment(f.properties.LAST_EDIT, 'M/D/YYYY').format('YYYY-MM-DD')}`)
                        .reduce(uniqueReducer, [])
                        .sort((a, b) => a.localeCompare(b))
                        .map(compositeGridId => {
                            const [gridId, lastEdited] = compositeGridId.split(':');
                            return{ gridId, lastEdited };
                        }),

                    airspaceClasses: relevantIdentAvisories.map(f => f.properties.UASFM_AIRSPACE).reduce(uniqueReducer, []) as IAirspaceClassification[],
                    maxAltitude: Math.min(...relevantIdentAvisories.map(f => f.properties.CEILING)),
                    UASFM_Enabled: this.getUASFM_Enabled(relevantIdentAvisories),
                    advisoryType: 'advise',
                    isLicensed: mission.properties.mission.properties.isLicensed
                }, initializedStatusMeta);

                const airspaceProps = mission.properties.airspaceFeatures.map(f => f.properties).find(a => a.GLOBAL_ID === airspaceGlobalId)!;

                // if it is active (because activeTimes is undefined)
                if (!airspaceProps.activeTimes) {
                    result.push(mergedPolygon as Feature<Polygon, AuthorizableLAANCAdvisoryProperties>);
                }
                // if there are active times, then we want to create authorization for each
                // time window
                else if (airspaceProps.activeTimes.length > 0) {

                    for (const active of airspaceProps.activeTimes) {
                        const clone = JSON.parse(JSON.stringify(mergedPolygon));
                        clone.properties = new AuthorizableLAANCAdvisoryProperties(clone.properties, initializedStatusMeta);

                        // if the schedule starts after the mission, then use it, since it is uncontrolled til then
                        const missionStartTime = mission.properties.mission.properties.startTime;
                        clone.properties.startTime = active.startTime > missionStartTime ? active.startTime : missionStartTime;

                        // if the schedule ends before the mission ends, then use it, since it is only controlled til then
                        const missionEndTime = mission.properties.mission.properties.endTime;
                        clone.properties.endTime = active.endTime < missionEndTime ? active.endTime : missionEndTime;

                        result.push(clone as Feature<Polygon, AuthorizableLAANCAdvisoryProperties>);
                    }
                }
                // if activeTimes.length === 0 then this is not active, and not authorizations needed
            }
        }

        return result;
    }

    /**
     * While we expect that laanc advisories are always a polygon, we observed at least once where they were not.
     * Likely due to some anomaly in intersecting with the radius. So here we fanout to many polygons if there
     * were any MultiPolygons.
     */
    private fanOutMultiPolygon = (laancAdvisories: Feature<Polygon | MultiPolygon, ILAANCAdvisoryProperties>[]): Feature<Polygon, ILAANCAdvisoryProperties>[] => {
        const output: Feature<Polygon, ILAANCAdvisoryProperties>[] = [];
        for (const advisory of laancAdvisories){
            if (advisory.geometry.type === 'MultiPolygon'){
                for (const polygonCoordinates of advisory.geometry.coordinates){
                    output.push({
                        ...advisory,
                        geometry: {
                            type: 'Polygon',
                            coordinates: polygonCoordinates
                        }
                    });
                }
            } else {
                output.push(advisory as Feature<Polygon, ILAANCAdvisoryProperties>);
            }
        }

        return output;
    }

    private getUASFM_Enabled = (laancAdvisories: Feature<Polygon | MultiPolygon, ILAANCAdvisoryProperties>[]): ILAANCEanbledFor[] => {
        const doAllAllow107AA = laancAdvisories.every(f => f.properties.UASFM_Enabled.includes('107-AA'));
        const doAllAllow107FC = laancAdvisories.every(f => f.properties.UASFM_Enabled.includes('107-FC'));
        const doAllAllow44809FC = laancAdvisories.every(f => f.properties.UASFM_Enabled.includes('44809-AA'));

        const response: ILAANCEanbledFor[] = [];
        if (doAllAllow107AA) {
            response.push('107-AA');
        }
        if (doAllAllow107FC) {
            response.push('107-FC');
        }
        if (doAllAllow44809FC) {
            response.push('44809-AA');
        }

        return response;
    }
}

const uniqueReducer = (acc: string[], item: string): string[] => {
    if (!acc.includes(item)) {
        acc.push(item);
    }
    return acc;
};
